import React, { useContext } from 'react'
import { ThemeProvider } from 'styled-components'
import { human, robotoWeights } from 'react-typography'
import '@fontsource/roboto'

const fontFamily = 'Roboto'

export const DefaultTheme = {
  colors: {
    accountDisclaimer: '#e7eef4',
    appBackgroundColor: '#f9f9f9',
    barStyle: 'light-content',
    black: '#000000',
    borderColor: '#dcdcdc;',
    boxShadowColor: 'rgba(0, 0, 0, 0.5)',
    cardBanner: '#51b135',
    darkGray: '#777777',
    defaultTextColor: '#13223C',
    destructiveAction: '#fe4a4a',
    disabledGrey: '#e7e7e7',
    disabledLightOutline: '#cccccc',
    disabledSubmitButton: '#1A54A8',
    droop: '#0070B9',
    droopText: '#ffffff',
    fastInnerRingColor: '#00B034',
    fastOuterRingColor: '#CCEFD6',
    flatListDivider: 'rgba(0, 0, 0, 0.3)',
    green: '#4CD964',
    headerTextColor: '#ffffff',
    headerTintColor: '#ffffff',
    headerTitleColor: '#ffffff',
    highlightTextColor: '#222121',
    highlightUnderlay: 'rgba(255, 255, 255, 0.3)',
    innerRingColor: '#2C70B3',
    inputError: '#fe4a4a',
    legalTextColor: '#ffffff',
    lightBackground: '#f9f9f9',
    lightGray: '#adadad',
    lightOutline: '#d6d6d6',
    lightTextColor: '#596981',
    loadingIndicator: '#111',
    outerRingColor: '#D1E3EF',
    placeholderTextColor: '#b2b2b2',
    primary: '#0070B9',
    progressStokeColor: '#2C70B3',
    scanDialColor: '#0066CC',
    separator: '#dddddd',
    slowInnerRingColor: '#FF9F00',
    slowOuterRingColor: '#FFE7CC',
    svgAccentColor: '#ffffff',
    unstableInnerRingColor: '#959595',
    unstableOuterRingColor: '#E7E7E7',
    white: '#ffffff',
    darkBackground: '#EEEEEE',
    descriptionColor: '#808080',
    slowTextColor: '#9D5E00',
    fastTextColor: '#035E1D',
    averageTextColor: '#3C4D6A',
    slowBgColor: 'rgba(225, 109, 1, 0.10)',
    fastBgColor: 'rgba(5, 156, 47, 0.10)',
    averageBgColor: 'rgba(242, 242, 242, 0.10)',
  },
  textStyles: {
    rb34: {
      ...human.caption1Object,
      ...robotoWeights.bold,
      fontSize: '34px',
      fontFamily,
    },
    rm48: {
      ...human.caption1Object,
      ...robotoWeights.medium,
      fontSize: '48px',
      fontFamily,
    },
    rb11: {
      ...human.caption1Object,
      ...robotoWeights.bold,
      fontSize: '11px',
      fontFamily,
    },
    rm20: {
      ...human.caption1Object,
      ...robotoWeights.medium,
      fontSize: '20px',
      fontFamily,
    },
    rb24: {
      ...human.caption1Object,
      ...robotoWeights.bold,
      fontSize: '24px',
      fontFamily,
    },
    rm16: {
      ...human.caption1Object,
      ...robotoWeights.medium,
      fontSize: '16px',
      fontFamily,
    },
    rm18: {
      ...human.caption1Object,
      ...robotoWeights.medium,
      fontSize: '18px',
      fontFamily,
    },
    rr14: {
      ...human.caption1Object,
      ...robotoWeights.regular,
      fontSize: '14px',
      fontFamily,
    },
    // rn14: {
    //   ...human.caption1Object,
    //   ...robotoWeights.medium,
    //   fontSize: '14px',
    //   fontFamily,
    // },
    rr16: {
      ...human.caption1Object,
      ...robotoWeights.regular,
      fontSize: '16px',
      fontFamily,
    },
    rm14: {
      ...human.caption1Object,
      ...robotoWeights.medium,
      fontSize: '14px',
      fontFamily,
    },
    rm12: {
      ...human.caption1Object,
      ...robotoWeights.regular,
      fontSize: '12px',
      fontFamily,
    },
    rb14: {
      ...human.caption1Object,
      ...robotoWeights.bold,
      fontSize: '14px',
      fontFamily,
    },
    rm32: {
      ...human.caption1Object,
      ...robotoWeights.medium,
      fontSize: '32px',
      fontFamily,
    },
    rb10: {
      ...human.caption1Object,
      ...robotoWeights.medium,
      fontSize: '10px',
      fontFamily,
    },
  },
}

export type AppTheme = typeof DefaultTheme

export const DeviceSupportTheme: AppTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#E60000',
    disabledSubmitButton: '#CACACA',
    progressStokeColor: '#E60000',
    outerRingColor: '#FACCCC',
    innerRingColor: '#E60000',
  },
}

export const HNTheme: AppTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#005796',
    progressStokeColor: '#005796',
    innerRingColor: '#005796',
  },
}

export const DirecTVTheme: AppTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#102641',
    progressStokeColor: '#102641',
    outerRingColor: '#10264150',
    innerRingColor: '#102641',
    fastOuterRingColor: '#E3D1E3',
    fastInnerRingColor: '#7A267B',
  },
}

export const TechExpertTheme: AppTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#E60000',
    disabledSubmitButton: '#CACACA',
    progressStokeColor: '#E60000',
    outerRingColor: '#FACCCC',
    innerRingColor: '#E60000',
  },
}

export const TmoPghTheme: AppTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#E20074',
    disabledSubmitButton: '#CACACA',
    progressStokeColor: '#E20074',
    outerRingColor: '#FACCCC',
    innerRingColor: '#E20074',
  },
}

export const XtdpTheme: AppTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#5A23B9',
    disabledSubmitButton: '#AC92DC',
    progressStokeColor: '#5A23B9',
    outerRingColor: '#AC92DC',
    innerRingColor: '#5A23B9',
  },
}

export const VodafonePortugalTheme: AppTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#E60000',
    disabledSubmitButton: '#CACACA',
    progressStokeColor: '#E60000',
    outerRingColor: '#FACCCC',
    innerRingColor: '#E60000',
  },
}

interface ThemedProps {
  theme: AppTheme
}

type ThemeGetter<T> = (props: ThemedProps) => T

type ThemeGetters = {
  [T in keyof AppTheme]: {
    // Should be ThemeGetter<Theme[T][U]> but styled-components doesn't like
    // the font styles then because of `fontVariant`
    [U in keyof AppTheme[T]]: ThemeGetter<any>
  }
}

// Theme getter functions for convenience in styled-components
export const theme = Object.keys(DefaultTheme).reduce(
  (acc, key) => ({
    ...acc,
    [key]: Object.keys((DefaultTheme as any)[key]).reduce(
      (acc, innerKey) => ({
        ...acc,
        [innerKey]: ({ theme }: ThemedProps) => {
          const category = (theme as any)[key] || (DefaultTheme as any)[key]
          return category[innerKey]
        },
      }),
      {}
    ),
  }),
  {}
) as ThemeGetters

export const Theme = React.createContext<AppTheme>(DefaultTheme)

export const useTheme = () => useContext(Theme)

interface Props {
  theme?: AppTheme
  children: any
}

export const AppThemeProvider = ({ theme = DefaultTheme, children }: Props) => (
  <ThemeProvider theme={theme}>
    <Theme.Provider value={theme}>{children}</Theme.Provider>
  </ThemeProvider>
)
